import { Loader } from 'ui';
import { DateSelection } from './DateSelection';
import { TimeSelection } from './TimeSelection';
import { AgreedCheckbox } from './AgreedCheckbox';
import { RecommendRound } from './RecommendRound';
import React, { useEffect, useState } from 'react';
import { findRemainingAttempts, isUserCollegeUser } from 'shared-utils';
import { RecommendTechStack } from './RecommendTechStack';
import { useUserContext } from 'src/context/UserProvider';
import DesktopPaymentButtons from './DesktopPaymentButtons';
import { DayTimeSlot, InterviewPathType, MeetingStatus, MeetingType, SlotTime, UIRoundTemplate } from 'types';
import { DesktopScreenCompanyBanner } from './DesktopScreenCompanyBanner';
import DesktopPaidPaymentButtons from './DesktopPaidPaymentButtons';
import AIInterviewScheduleStep from './AIInterviewScheduleStep';
import { Collapse, useMediaQuery } from '@mui/material';
import { Badge, Button, Label } from 'flowbite-react';
import classNames from 'classnames';
import { GoDotFill } from 'react-icons/go';
import CaptureAndUploadProfileImage from '../profile/CaptureAndUploadProfileImage';
import BrowserWarningModal from './BrowserWarningModal';
import { browserName } from 'react-device-detect';
import { Img } from 'react-image';
import { HiArrowLeft } from 'react-icons/hi';

interface Props {
	isOpen?: boolean;
	selectedDaySlot?: DayTimeSlot;
	selectedTimeSlot?: SlotTime;
	setSelectedDaySlot: React.Dispatch<React.SetStateAction<DayTimeSlot | undefined>>;
	setSelectedTimeSlot: React.Dispatch<React.SetStateAction<SlotTime | undefined>>;
	allSlots: DayTimeSlot[];
	setSelectedTechStack: (val: string) => void;
	setIsAIRoundSelected: (val: boolean) => void;
	selectedTeckStack: string;
	setSelectedRound: (val: string) => void;
	selectedRound: string;
	roundTemplates: UIRoundTemplate[];
	setCurrentStep: (val: number) => void;
	onClose: () => void;
	onSchedule: () => void;
	isLoading?: boolean;
	fetchingSlots?: boolean;
	handleScheduleInterviewForPaidUser: () => void;
	hasAlreadyPaidForInterview: boolean;
	isAIRoundSelected: boolean;
	showMockTestOnly?: boolean;
	setShowMockTestOnly?: React.Dispatch<React.SetStateAction<boolean>>;
	interviewType?: MeetingType | null;
	setInterviewType?: React.Dispatch<React.SetStateAction<MeetingType | null>>;
}

export const DesktopMeetingScheduleBody = ({
	onClose,
	isOpen,
	roundTemplates,
	setCurrentStep,
	setSelectedTechStack,
	selectedTeckStack,
	allSlots,
	setSelectedDaySlot,
	setSelectedTimeSlot,
	selectedDaySlot,
	selectedTimeSlot,
	selectedRound,
	onSchedule,
	setSelectedRound,
	isLoading,
	fetchingSlots,
	handleScheduleInterviewForPaidUser,
	hasAlreadyPaidForInterview,
	setIsAIRoundSelected,
	isAIRoundSelected,
	showMockTestOnly,
	setShowMockTestOnly,
	interviewType,
	setInterviewType,
}: Props) => {
	const { userRounds, userMeetings, hasReschedulableMeeting, latestScheduledMeeting, user, showAwaitingResults } =
		useUserContext();
	const isMobile = useMediaQuery('(max-width: 960px)');
	const [openPorfileUploadModal, setOpenProfileUploadModal] = useState(false);
	const [openBrowserWarningModal, setOpenBrowserWarningModal] = useState(false);

	const [hasAgreed, setHasAgreed] = useState(false);
	const [scheduleForLater, setScheduleForLater] = useState(isMobile);
	const [availableRounds, setAvailableRounds] = useState(userRounds);
	const isPaidUser = user?.evaluation?.interviewPath?.type === InterviewPathType.Paid;

	const shouldShowPaymentOption = isPaidUser && !hasAlreadyPaidForInterview && availableRounds.length === 0;

	const expiry = user?.evaluation?.interviewPath?.expiresAt;

	const totalMockAttempts = userRounds?.filter(r => r?.isMock && findRemainingAttempts(r.maxAttempts, r.meetings))
		?.length;

	const interviewTypes = [
		{
			title: 'Profile Picture',
			subTitle: 'Complete your profile by uploading your picture.',
			onClick: () => setOpenProfileUploadModal(true),
			ctaText: 'Upload',
			hide: user?.profile?.proctoringImageUrl,
			disabled: false,
			completed: user?.profile?.proctoringImageUrl,
		},
		{
			title: 'AI Interview',
			subTitle: 'The AI interview test is a 90-minute assessment designed to evaluate your skills comprehensively.',
			onClick: () => {
				browserName !== 'Chrome'
					? setOpenBrowserWarningModal(true)
					: setInterviewType && setInterviewType(MeetingType.Interview);
			},
			warning:
				browserName !== 'Chrome' ? (
					<div className="hidden md:flex items-center gap-2">
						<Img src="/assets/chromeLogo.svg" className="h-[32px] w-[32px]" />
						<p className="max-w-[195px] text-[10px] font-[400] text-[#555770] dark:text-gray-300">
							Please install the latest Chrome Browser for the interview.{' '}
							<a
								className="text-[#7777E7] dark:text-blue-400 "
								href="https://www.google.com/intl/en_in/chrome/"
								target="_blank"
							>
								Use Chrome
							</a>
						</p>
					</div>
				) : null,
			ctaText: 'Start Interview',
			disabled: !user?.profile?.proctoringImageUrl,
			pending: showAwaitingResults,
			completed:
				userMeetings?.filter(
					el =>
						el?.isAIInterview &&
						!el.isMock &&
						[MeetingStatus.COMPLETED].includes(el.meetingStatus) &&
						el?.isConsideredAttempt,
				)?.length &&
				userRounds.filter(
					r => findRemainingAttempts(r.maxAttempts, r.meetings) && !r.isMock && r.roundTemplate.isAIInterviewTemplate,
				)?.length === 0,
		},
		{
			title: 'Assessment Test',
			subTitle:
				'The 30-minute assessment test is designed to gauge your skills in puzzles, aptitude, reading comprehension, and data interpretation.',
			onClick: () => {
				setInterviewType && setInterviewType(MeetingType.Assessment);
			},
			ctaText: 'Take Test',
			disabled: !user?.profile?.proctoringImageUrl,
			completed:
				userMeetings?.filter(
					el =>
						!el?.isAIInterview &&
						!el?.isMock &&
						[MeetingStatus.COMPLETED].includes(el.meetingStatus) &&
						el?.isConsideredAttempt,
				)?.length &&
				userRounds.filter(
					r => findRemainingAttempts(r.maxAttempts, r.meetings) && !r.isMock && !r.roundTemplate.isAIInterviewTemplate,
				)?.length === 0,
		},
	];

	// console.log('roundTemplates:', roundTemplates);
	const disableStartInterView =
		availableRounds?.length ||
		roundTemplates?.filter(
			el =>
				(interviewType === MeetingType.Assessment
					? MeetingType.Assessment === el?.templateType
					: el.templateType !== MeetingType.Assessment) && (showMockTestOnly ? el.isMock : !el.isMock),
		)?.length
			? false
			: true;

	const scheduleComponent = (
		<>
			{!isAIRoundSelected ? (
				<AgreedCheckbox agreed={hasAgreed} setHasAgreed={setHasAgreed} styleing="text-[18px]" />
			) : null}
			{shouldShowPaymentOption ? (
				<DesktopPaidPaymentButtons
					onClose={onClose}
					hasAgreed={hasAgreed}
					handleScheduleInterviewForPaidUser={handleScheduleInterviewForPaidUser}
				/>
			) : (
				<DesktopPaymentButtons
					onSchedule={onSchedule}
					disabled={disableStartInterView}
					scheduleForLater={scheduleForLater}
					onScheduleForLater={() => setScheduleForLater(true)}
					selectedDaySlot={selectedDaySlot}
					selectedTimeSlot={selectedTimeSlot}
					isAIRoundSelected={isAIRoundSelected}
					selectedTeckStack={selectedTeckStack}
					setCurrentStep={setCurrentStep}
					onClose={onClose}
					hasReschedulableMeeting={hasReschedulableMeeting}
					selectedRound={selectedRound}
					isLoading={isLoading}
					hasAgreed={hasAgreed}
					showMockTestOnly={showMockTestOnly}
				/>
			)}
		</>
	);

	useEffect(() => {
		const filteredRound = userRounds.filter(
			r =>
				findRemainingAttempts(r.maxAttempts, r.meetings) &&
				(showMockTestOnly ? r.isMock : !r?.isMock) &&
				(!interviewType ||
					(interviewType === MeetingType.Assessment
						? interviewType === r.meetingType
						: r.meetingType !== MeetingType.Assessment)),
		);

		setAvailableRounds(filteredRound);
		if (filteredRound?.length && filteredRound[0]?._id) setSelectedRound(filteredRound[0]._id);
		else setSelectedRound('');
	}, [userRounds, interviewType, showMockTestOnly, isOpen]);

	useEffect(() => {
		if (isAIRoundSelected && !selectedTimeSlot) setSelectedTimeSlot({ time: new Date(), count: 1 });
	}, [isAIRoundSelected, selectedTimeSlot]);

	useEffect(() => {
		return () => {
			setScheduleForLater(isMobile);
			setHasAgreed(false);
			setAvailableRounds(
				userRounds.filter(
					r =>
						findRemainingAttempts(r.maxAttempts, r.meetings) &&
						(showMockTestOnly ? r.isMock : !r?.isMock) &&
						(!interviewType ||
							(interviewType === MeetingType.Assessment
								? interviewType === r.meetingType
								: r.meetingType !== MeetingType.Assessment)),
				),
			);
		};
	}, [isOpen]);

	return (
		<>
			{showMockTestOnly || (user && !isUserCollegeUser(user)) ? (
				<div className="font-inter font-[500] text-[#1F3343] dark:text-[#FFFFFF] leading-[28px] pb-4 text-[20px] mt-6 dark:border-b-[#8F90A6] border-b-[#E4E4EB] border-b-2 py-2">
					{hasReschedulableMeeting ? 'Reschedule' : 'Schedule'} your round of{' '}
					{userRounds.find(ur => String(ur._id) === selectedRound)?.roundTemplate.duration ??
						latestScheduledMeeting?.duration ??
						roundTemplates.find(rT => String(rT._id) === selectedTeckStack)?.duration ??
						0}{' '}
					mins
				</div>
			) : (
				<div className="flex items-center font-inter font-[500] text-[#1F3343] dark:text-[#FFFFFF] leading-[28px] pb-4 text-[20px] mt-6 dark:border-b-[#8F90A6] border-b-[#E4E4EB] border-b-2 py-2">
					<div>
						<Collapse in={!!interviewType} orientation="horizontal">
							<button className="flex items-center mr-3" onClick={() => setInterviewType && setInterviewType(null)}>
								<HiArrowLeft />
							</button>
						</Collapse>
					</div>
					Start your <strong className="ml-2">Skills assessment</strong>
				</div>
			)}
			<div className="flex justify-between gap-4 py-2 mt-3">
				<div className="max-w-[68%] flex flex-col gap-8 w-full">
					{user && isUserCollegeUser(user) && !showMockTestOnly && !interviewType ? (
						<div className="flex flex-col h-full">
							<div className="flex flex-col">
								{interviewTypes
									?.filter(el => !el?.hide)
									?.map((el, index) => (
										<div key={index} className="flex gap-4">
											<div className="relative isolate">
												{el?.completed ? (
													<img src="/assets/prepareBadgeCompleted.svg" className="w-6 h-6 bg-white dark:bg-[#111E2C]" />
												) : (
													<img src="/assets/prepareBadgeInProgress.svg" className="w-6 h-6" />
												)}
												{index < interviewTypes?.filter(el => !el?.hide)?.length - 1 ? (
													<div
														className={classNames(
															'w-[2px] absolute inset-x-0 -z-[2] border-l-[2px] border-dashed border-[#6698FA] mx-auto',
															index === 0 ? 'h-[120%] top-[-20px]' : 'h-full top-0',
														)}
													></div>
												) : null}
											</div>
											<div className="flex flex-col pb-[40px]">
												<div className="text-[16px] font-[500] text-[#1C1C28] dark:text-white">{el?.title}</div>
												<div className="mt-1 text-[#555770] dark:text-gray-400 font-[400] text-[12px] max-w-[412px]">
													{el?.subTitle}
												</div>

												{el?.pending ? (
													<Badge
														size="xs"
														color="warning"
														className="w-fit px-[10px] mt-2"
														icon={() => <GoDotFill className="scale-[1.5]" />}
													>
														Result Pending
													</Badge>
												) : el?.completed ? (
													<Badge
														color="green"
														className="w-fit mt-2"
														icon={() => <GoDotFill className="scale-[1.5] text-[#0E9F6E]" />}
													>
														Completed
													</Badge>
												) : (
													<div className="flex items-center gap-3 mt-4">
														<Button
															color="purplePlaced"
															className="max-w-[167px]"
															disabled={el.disabled}
															onClick={el?.onClick}
														>
															{el?.ctaText}
														</Button>

														{el?.warning ? el?.warning : null}
													</div>
												)}
											</div>
										</div>
									))}
							</div>
							{totalMockAttempts ? (
								<div className="text-[#8F90A6] font-[500] text-[14px] pb-[36px]">
									Not confident yet?{' '}
									<button
										className="text-[#7777E7] underline"
										onClick={() => {
											setInterviewType && setInterviewType(null);
											setShowMockTestOnly && setShowMockTestOnly(true);
										}}
									>
										Try Mock Test
									</button>
								</div>
							) : null}
						</div>
					) : (
						<>
							{availableRounds.length ? (
								!hasReschedulableMeeting && (
									<div>
										<RecommendRound
											interviewType={interviewType}
											rounds={availableRounds}
											showMockTestOnly={showMockTestOnly}
											setSelectedRound={setSelectedRound}
											setIsAIRoundSelected={setIsAIRoundSelected}
											selectedRound={selectedRound}
										/>
									</div>
								)
							) : !hasReschedulableMeeting ? (
								<div>
									<RecommendTechStack
										interviewType={interviewType}
										roundTemplates={roundTemplates}
										showMockTestOnly={showMockTestOnly}
										setSelectedTechStack={setSelectedTechStack}
										setIsAIRoundSelected={setIsAIRoundSelected}
										selectedTeckStack={selectedTeckStack}
									/>
								</div>
							) : null}

							{(selectedRound &&
								userRounds?.find(el => el?._id?.toString() === selectedRound?.toString())?.description) ||
							(selectedTeckStack &&
								roundTemplates?.find(el => el?._id?.toString() === selectedTeckStack?.toString())?.description) ? (
								<div>
									<p className="text-gray-500 dark:text-gray-400 italic text-sm">Description</p>

									{selectedRound
										? userRounds?.find(el => el?._id?.toString() === selectedRound?.toString())?.description
										: selectedTeckStack
										  ? roundTemplates?.find(el => el?._id?.toString() === selectedTeckStack?.toString())?.description
										  : null}
								</div>
							) : null}

							{isAIRoundSelected ? (
								<>
									{scheduleForLater ? (
										<AIInterviewScheduleStep
											selectedTimeSlot={selectedTimeSlot ?? { time: new Date(), count: 1 }}
											setSelectedTimeSlot={setSelectedTimeSlot}
										/>
									) : null}
									{scheduleComponent}
								</>
							) : fetchingSlots ? (
								<Loader />
							) : allSlots.length > 0 ? (
								<>
									<div className="flex flex-col gap-4">
										<DateSelection
											allSlots={allSlots}
											selectedDaySlot={selectedDaySlot}
											setSelectedDaySlot={(value: DayTimeSlot) => {
												setSelectedDaySlot(value);
												setSelectedTimeSlot(undefined);
											}}
										/>
										<TimeSelection
											selectedDaySlot={selectedDaySlot}
											selectedTimeSlot={selectedTimeSlot}
											setSelectedTimeSlot={setSelectedTimeSlot}
										/>
									</div>
									{scheduleComponent}
								</>
							) : (
								<div className="flex h-full w-full justify-center items-center dark:text-[#C7C9D9]">
									No available slots found
								</div>
							)}
						</>
					)}
				</div>
				<div className="basis-[30%] max-w-[30%]">
					<DesktopScreenCompanyBanner expiry={expiry} isAIRoundSelected={isAIRoundSelected} />
				</div>
			</div>

			<CaptureAndUploadProfileImage isOpen={openPorfileUploadModal} onClose={() => setOpenProfileUploadModal(false)} />
			<BrowserWarningModal
				openModal={openBrowserWarningModal}
				setOpenModal={() => {
					setOpenBrowserWarningModal(false);
				}}
				isJoinedFromMobile={isMobile}
			/>
		</>
	);
};
